/**
 * Customer Accounts container (C22, B4 — D03 §5): binds `CustomerAccounts` (packages/ui, App.tsx:33771-34270) and its
 * dialogs to the read model (`GET /customers`, archived list, statements) and posts every money change through the
 * gateway (`useGateway`: preview → PIN → commit, D-018, D-232). Ids only (D-017): rows, pickers and statements never
 * match by name. Lists, header cards and statements read the same ledger figures (D-013, R-17 / R-21 fixed). Filter,
 * search (name, phone, account number, address — old `filteredDebts`) and the ten old sort orders are presentation
 * (ordering compares decimal strings, no money arithmetic). "Delete" archives at zero (D-072); a non-zero account opens
 * the "settle first" dialog. USD bits follow the office setting (D-066).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArchiveBlockedModal, ArchivedCustomersModal, CustomerAccounts, type CaFilter, type CaRow, type CaSort } from "@faylasuf/ui";
import type { CustomerAccountRow } from "../api/types.js";
import { useApp, useSession } from "../app/context.js";
import { errorMessage, isSessionLost } from "../app/errors.js";
import { usePinGate } from "../app/pin-gate.js";
import { keys } from "../app/query.js";
import type { SectionFlags } from "../app/sections.js";
import { useToast } from "../app/toast.js";
import { useAccounts } from "./WalletsPage.js";
import { AddContainer } from "./customers/AddContainer.js";
import { AdjustContainer } from "./customers/AdjustContainer.js";
import { customerBoxes } from "./customers/boxes.js";
import { ConvertContainer } from "./customers/ConvertContainer.js";
import { CustomerStatementContainer } from "./customers/CustomerStatementContainer.js";
import { DualStatementContainer } from "./customers/DualStatementContainer.js";
import { EditContainer } from "./customers/EditContainer.js";
import { activityDate, activityKey, compareAccountNumbers, dateDashes, decCmp, fmt, isZero } from "./customers/format.js";
import { SettleContainer } from "./customers/SettleContainer.js";
import { TransferContainer, type TransferPreset } from "./customers/TransferContainer.js";

type Modal =
  | { readonly k: "add" }
  | { readonly k: "adjust"; readonly id: string; readonly action: "add" | "withdraw" }
  | { readonly k: "settle"; readonly id: string }
  | { readonly k: "convert"; readonly id: string }
  | { readonly k: "transfer"; readonly preset: TransferPreset | null }
  | { readonly k: "edit"; readonly id: string }
  | { readonly k: "blocked"; readonly id: string }
  | { readonly k: "dual" }
  | { readonly k: "archived" };

function sortRows(list: CustomerAccountRow[], sort: CaSort): CustomerAccountRow[] {
  const lastAt = (r: CustomerAccountRow) => r.lastActivity?.at ?? r.createdAt;
  const by: Record<CaSort, (a: CustomerAccountRow, b: CustomerAccountRow) => number> = {
    default: (a, b) => a.number - b.number,
    recent_activity: (a, b) => lastAt(b).localeCompare(lastAt(a)),
    highest_balance: (a, b) => decCmp(b.iqd.balance, a.iqd.balance),
    lowest_balance: (a, b) => decCmp(a.iqd.balance, b.iqd.balance),
    alphabetical: (a, b) => a.name.localeCompare(b.name, "ar"),
    alphabetical_desc: (a, b) => b.name.localeCompare(a.name, "ar"),
    account_number_asc: (a, b) => compareAccountNumbers(a.accountNumber, b.accountNumber),
    account_number_desc: (a, b) => compareAccountNumbers(b.accountNumber, a.accountNumber),
    created_desc: (a, b) => b.createdAt.localeCompare(a.createdAt),
    created_asc: (a, b) => a.createdAt.localeCompare(b.createdAt),
  };
  return [...list].sort(by[sort]);
}

/** `initialFilter` [C26]: the dashboard debt / trust cards open the list filtered (App.tsx:26880-26935). */
export function CustomersPage({ flags, themeColor, addRequest, initialFilter = "all" }: { flags: SectionFlags; themeColor: string; addRequest: number; initialFilter?: CaFilter }) {
  const { t, source, locale, now } = useApp();
  const { session, hasPermission, sessionLost } = useSession();
  const toast = useToast();
  const pin = usePinGate();
  const qc = useQueryClient();
  const tz = session.office.timeZone;

  const [filter, setFilter] = useState<CaFilter>(initialFilter);
  const [sort, setSort] = useState<CaSort>("default");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState<Modal | null>(null);
  const [statement, setStatement] = useState<{ id: string; autoPrint: boolean } | null>(null);

  const activeQuery = useQuery({ queryKey: keys.customers(false), queryFn: () => source.customers(false) });
  const archivedQuery = useQuery({ queryKey: keys.customers(true), queryFn: () => source.customers(true) });
  const accountsQuery = useAccounts(true);
  for (const e of [activeQuery.error, archivedQuery.error]) if (e && isSessionLost(e)) sessionLost();

  // The header's "إضافة جديد" (Shell) bumps `addRequest`.
  const lastAdd = useRef(addRequest);
  useEffect(() => {
    if (addRequest !== lastAdd.current) {
      lastAdd.current = addRequest;
      setModal({ k: "add" });
    }
  }, [addRequest]);

  const usdHidden = activeQuery.data?.usdHidden ?? flags.usdHidden;
  const rows = activeQuery.data?.rows ?? [];
  const archivedRows = archivedQuery.data?.rows ?? [];
  const boxes = useMemo(() => customerBoxes(accountsQuery.data ?? [], session, flags.subSafesActive, t), [accountsQuery.data, session, flags.subSafesActive, t]);
  const findRow = (id: string) => rows.find((r) => r.id === id) ?? archivedRows.find((r) => r.id === id);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = rows.filter((d) => {
      if (filter !== "all" && d.kind !== filter) return false;
      if (!q) return true;
      return d.name.toLowerCase().includes(q) || (d.phone ?? "").toLowerCase().includes(q) || d.accountNumber.toLowerCase().includes(q) || (d.address ?? "").toLowerCase().includes(q);
    });
    return sortRows(filtered, sort);
  }, [rows, filter, search, sort]);

  const nowDate = now();
  const caRows: CaRow[] = shown.map((d) => ({
    id: d.id,
    numberLabel: String(d.number),
    name: d.name,
    accountNumber: d.accountNumber,
    phone: d.phone,
    address: d.address,
    kind: d.kind,
    iqdLabel: `${fmt(d.iqd.balance)} ${t("unit.iqd")}`,
    usdLabel: !usdHidden && !isZero(d.usd.balance) ? `${fmt(d.usd.balance)} $` : null,
    activity: d.lastActivity
      ? {
          dateLabel: activityDate(d.lastActivity.at, nowDate, tz, locale, t("cust.today")),
          label: t(activityKey(d.lastActivity.type)),
          title: d.lastActivity.note ? `${t(activityKey(d.lastActivity.type))} - ${d.lastActivity.note}` : t(activityKey(d.lastActivity.type)),
        }
      : null,
    createdLabel: dateDashes(d.createdAt, tz),
  }));

  const totals = activeQuery.data?.totals;
  const positiveUsd = (s: string | undefined) => (s !== undefined && decCmp(s, "0") > 0 ? fmt(s) : null);

  const archive = async (row: CustomerAccountRow, doArchive: boolean) => {
    try {
      const res = await pin.withPin((proof) => source.archiveCustomer(row.id, doArchive, proof.pin ?? ""), {
        message: doArchive ? t("cust.pin_archive", { name: row.name }) : t("cust.pin_restore", { name: row.name }),
        allowSupervisor: false,
      });
      if (!res) return;
      toast(doArchive ? t("cust.archived_done") : t("cust.restored_done"), "success");
      void qc.invalidateQueries({ queryKey: ["customers"] });
      void qc.invalidateQueries({ queryKey: ["accounts"] });
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else toast(errorMessage(t, e), "error");
    }
  };

  const onArchive = (id: string) => {
    const row = findRow(id);
    if (!row) return;
    if (!isZero(row.iqd.balance) || !isZero(row.usd.balance)) setModal({ k: "blocked", id });
    else void archive(row, true);
  };

  const modalRow = modal && "id" in modal ? findRow(modal.id) : undefined;
  const statementRow = statement ? findRow(statement.id) : undefined;
  const close = () => setModal(null);

  return (
    <>
      <CustomerAccounts
        totals={{
          owedIqdLabel: fmt(totals?.owedIqd ?? "0"),
          owedUsdLabel: positiveUsd(totals?.owedUsd),
          creditIqdLabel: fmt(totals?.creditIqd ?? "0"),
          creditUsdLabel: positiveUsd(totals?.creditUsd),
        }}
        counts={{ all: rows.length, debt: rows.filter((r) => r.kind === "debt").length, trust: rows.filter((r) => r.kind === "trust").length }}
        filter={filter}
        onFilterChange={setFilter}
        sort={sort}
        onSortChange={setSort}
        search={search}
        onSearchChange={setSearch}
        rows={caRows}
        usdHidden={usdHidden}
        canEdit={hasPermission("show_customer_delete_edit") && hasPermission("edit_customers")}
        canArchive={hasPermission("show_customer_delete_edit") && hasPermission("delete_customers")}
        archivedCount={archivedRows.length}
        onOpenArchived={() => setModal({ k: "archived" })}
        onTransfer={() => setModal({ k: "transfer", preset: null })}
        onDualStatement={() => setModal({ k: "dual" })}
        onStatement={(id) => setStatement({ id, autoPrint: false })}
        onSharePdf={(id) => setStatement({ id, autoPrint: true })}
        onTransferFrom={(id) => setModal({ k: "transfer", preset: { fromId: id } })}
        onConvert={(id) => {
          if (!activeQuery.data?.usdQuotes) toast(t("cust.convert_no_quotes"), "info");
          setModal({ k: "convert", id });
        }}
        onAdjust={(id, action) => setModal({ k: "adjust", id, action })}
        onSettle={(id) => setModal({ k: "settle", id })}
        onEdit={(id) => setModal({ k: "edit", id })}
        onArchive={onArchive}
      />
      {modal?.k === "add" && (
        <AddContainer
          boxes={boxes}
          usdHidden={usdHidden}
          kindDefault={filter === "all" ? "debt" : filter}
          kindLocked={filter !== "all"}
          themeColor={themeColor}
          onClose={close}
          onViewStatement={(id) => {
            close();
            setStatement({ id, autoPrint: false });
          }}
          onRestore={(id) => {
            const row = findRow(id);
            close();
            if (row) void archive(row, false);
          }}
        />
      )}
      {modal?.k === "adjust" && modalRow && <AdjustContainer key={`${modalRow.id}-${modal.action}`} row={modalRow} rows={rows} boxes={boxes} usdHidden={usdHidden} initialAction={modal.action} onClose={close} />}
      {modal?.k === "settle" && modalRow && <SettleContainer row={modalRow} boxes={boxes} usdHidden={usdHidden} onClose={close} />}
      {modal?.k === "convert" && modalRow && <ConvertContainer row={modalRow} quotes={activeQuery.data?.usdQuotes ?? null} onClose={close} />}
      {modal?.k === "transfer" && <TransferContainer rows={rows} usdHidden={usdHidden} preset={modal.preset} onClose={close} />}
      {modal?.k === "edit" && modalRow && (
        <EditContainer
          row={modalRow}
          themeColor={themeColor}
          onClose={close}
          onViewStatement={(id) => {
            close();
            setStatement({ id, autoPrint: false });
          }}
        />
      )}
      {modal?.k === "blocked" && modalRow && (
        <ArchiveBlockedModal
          name={modalRow.name}
          iqdLabel={fmt(modalRow.iqd.balance)}
          usdLabel={fmt(modalRow.usd.balance)}
          usdHidden={usdHidden}
          onSettle={() => setModal({ k: "settle", id: modalRow.id })}
          onTransfer={() =>
            setModal({
              k: "transfer",
              preset: {
                fromId: modalRow.id,
                currency: !isZero(modalRow.iqd.balance) ? "IQD" : "USD",
                amount: (!isZero(modalRow.iqd.balance) ? modalRow.iqd.balance : modalRow.usd.balance).replace(/^-/, ""),
                note: t("cust.transfer_before_archive", { name: modalRow.name }),
              },
            })
          }
          onClose={close}
        />
      )}
      {modal?.k === "dual" && <DualStatementContainer rows={rows} usdHidden={usdHidden} themeColor={themeColor} onClose={close} />}
      {modal?.k === "archived" && (
        <ArchivedCustomersModal
          rows={archivedRows.map((r) => ({ id: r.id, name: r.name, accountNumber: r.accountNumber, kind: r.kind, archivedLabel: r.archivedAt ? dateDashes(r.archivedAt, tz) : "-" }))}
          canRestore={hasPermission("show_customer_delete_edit") && hasPermission("delete_customers")}
          onStatement={(id) => setStatement({ id, autoPrint: false })}
          onRestore={(id) => {
            const row = findRow(id);
            if (row) void archive(row, false);
          }}
          onClose={close}
        />
      )}
      {statement && statementRow && (
        <CustomerStatementContainer key={`${statement.id}-${statement.autoPrint}`} account={statementRow} usdHidden={usdHidden} autoPrint={statement.autoPrint} onClose={() => setStatement(null)} />
      )}
      {pin.dialog}
    </>
  );
}
